import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";

export default function WebsiteGalleryPage() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [error, setError] = useState();
    const [record, setRecord] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: "website/gallery-page",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRecord(data);
                else setError("Couldn't load Gallery Page");
            },
        });
    }, [requestAPI]);

    const saveRecord = () => {
        requestAPI({
            requestPath: "website/gallery-page",
            requestMethod: "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Gallery Page !", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    setRecord(updated);
                    showToast({ severity: "success", summary: "Updated", detail: "Gallery Page Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Gallery Page !", life: 2000 });
                }
            },
        });
    };

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Gallery Page" />
            <TabHeader
                className="px-3 pt-3"
                title="Gallery page text"
                highlights={["These words appear at the top of the Gallery page, above the folders"]}
            />
            <Divider />
            {loading ? <Loading /> : null}
            {error ? <NoContent error={error} /> : null}
            {!loading && !error && record ? (
                <div className="flex-1 overflow-y-scroll p-3 flex flex-column">
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="eyebrow" className="w-full" value={record?.eyebrow ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, eyebrow: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="eyebrow">Small line above the heading</label>
                        </FloatLabel>
                        <FieldNote text="Example: SAHAS Campus & Memories." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="title">Page heading</label>
                        </FloatLabel>
                        <FieldNote text="The large heading at the top of the Gallery page." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                            <label htmlFor="description">Line under the heading</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="categories_eyebrow" className="w-full" value={record?.categories_eyebrow ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, categories_eyebrow: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="categories_eyebrow">Small line above the folders</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="categories_title" className="w-full" value={record?.categories_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, categories_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="categories_title">Heading above the folders</label>
                        </FloatLabel>
                        <FieldNote text="Example: Explore Our Gallery." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="categories_description" className="w-full" rows={4} autoResize value={record?.categories_description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, categories_description: event.target.value }))} disabled={saving} />
                            <label htmlFor="categories_description">Line under the folder heading</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="featured_eyebrow" className="w-full" value={record?.featured_eyebrow ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, featured_eyebrow: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="featured_eyebrow">Small line above the featured event</label>
                        </FloatLabel>
                        <FieldNote text="Shown only when one event is marked as featured." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="empty_text" className="w-full" rows={3} autoResize value={record?.empty_text || ""} onChange={(event) => setRecord((prev) => ({ ...prev, empty_text: event.target.value }))} disabled={saving} />
                            <label htmlFor="empty_text">Message when there are no folders</label>
                        </FloatLabel>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="show_search" checked={!!record?.show_search} onChange={(event) => setRecord((prev) => ({ ...prev, show_search: event.checked }))} disabled={saving} />
                        <label htmlFor="show_search" className={TEXT_SMALL}>Show the search box</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="show_filters" checked={!!record?.show_filters} onChange={(event) => setRecord((prev) => ({ ...prev, show_filters: event.checked }))} disabled={saving} />
                        <label htmlFor="show_filters" className={TEXT_SMALL}>Show a chip for each folder</label>
                    </div>
                    <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={saveRecord} />
                </div>
            ) : null}
        </div>
    );
}
